/**
 * TimerDisplay Component
 *
 * Displays the countdown timer for a player
 */

import React, { useEffect, useState } from 'react';
import styles from './TimerDisplay.module.css';
import { TimeControlManager, formatTime } from '../../services/timeControl';

interface TimerDisplayProps {
  player: 'light' | 'dark';
  isActive: boolean;
}

const TimerDisplay: React.FC<TimerDisplayProps> = ({ player, isActive }) => {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    // Subscribe to time updates
    const unsubscribe = TimeControlManager.subscribe((event) => {
      const time = player === 'light' ? event.lightTimeRemaining : event.darkTimeRemaining;
      setTimeRemaining(time);
      setIsPaused(event.isPaused);
    });

    // Get initial time
    const initialTime = TimeControlManager.getTimeRemaining(player);
    setTimeRemaining(initialTime);

    return () => {
      unsubscribe();
    };
  }, [player]);

  // Determine warning level based on time remaining
  const getWarningLevel = (): 'normal' | 'warning' | 'critical' => {
    const seconds = timeRemaining / 1000;
    if (seconds <= 10) return 'critical';
    if (seconds <= 30) return 'warning';
    return 'normal';
  };

  const warningLevel = getWarningLevel();
  const formattedTime = formatTime(timeRemaining);

  return (
    <div
      className={`${styles.timerDisplay} ${
        isActive ? styles.active : ''
      } ${styles[warningLevel]} ${isPaused ? styles.paused : ''}`}
    >
      <div className={styles.playerLabel}>
        {player === 'light' ? 'Light' : 'Dark'}
      </div>
      <div className={styles.timeValue}>{formattedTime}</div>
    </div>
  );
};

export default TimerDisplay;
